import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { ExpressMasterList } from "../../types/express-master.type";

const DEFAULT_LIMIT = 10;

export interface MasterListState<Row> {
  rows: Row[];
  totalCount: number;
  loading: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  limit: number;
  setLimit: Dispatch<SetStateAction<number>>;
  reload: () => void;
}

/**
 * A page of a master list for the given filters. `fetchPage` must keep its identity and
 * `filters` change only when a filter does; any filter change starts again from page 1.
 */
export function useMasterList<Row, Filters extends object>(
  fetchPage: (query: Filters & { page: number; limit: number }) => Promise<ExpressMasterList<Row>>,
  filters: Filters,
): MasterListState<Row> {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [rows, setRows] = useState<Row[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchPage({ ...filters, page, limit });
      setRows(list.rows);
      setTotalCount(list.count);
    } catch {
      // The interceptor already shows why the list could not be read.
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [fetchPage, filters, page, limit]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  return { rows, totalCount, loading, page, setPage, limit, setLimit, reload };
}
